<div class="template-wizard">
  @if (eventUrlChoiceLoading) {
    <section class="wizard-panel event-url-choice" aria-busy="true">
      <header class="panel-header">
        <h2>Indlæser</h2>
        <p>Henter oplysninger om eventet…</p>
      </header>
    </section>
  } @else if (showEventUrlChoice) {
    <section class="wizard-panel event-url-choice" aria-label="Kopier eller opret skabelon">
      <p class="choice-lead">
        Vil du kopiere dette event til nye datoer, eller oprette en skabelon
        med eventet som udgangspunkt?
      </p>
      <p class="choice-hint">
        Kopiering er en engangs-handling uden gemt skabelon. En skabelon kan
        genbruges senere.
      </p>

      <footer class="panel-footer choice-footer">
        <button mat-button type="button" (click)="dismissEventUrlChoice()">
          Til skabelonlisten
        </button>
        <button
          mat-stroked-button
          color="primary"
          type="button"
          (click)="chooseEventUrlCopyOnce()">
          Kopier event
        </button>
        <button
          mat-raised-button
          color="primary"
          type="button"
          (click)="chooseEventUrlCreateTemplate()">
          Opret skabelon
        </button>
      </footer>
    </section>
  } @else {
    <nav class="wizard-steps" aria-label="Wizard-trin">
      @for (label of stepLabels; track label; let i = $index) {
        <div
          class="wizard-step"
          [class.active]="wizardStep === i + 1"
          [class.completed]="wizardStep > i + 1">
          <span class="step-number">{{ i + 1 }}</span>
          <span class="step-label">{{ label }}</span>
        </div>
      }
    </nav>

    <!-- Trin 1: Vælg skabelon (holdes i DOM så tabellen kan genindlæses efter gem) -->
    <section class="wizard-panel" [hidden]="wizardStep !== 1">
        <header class="panel-header">
          <h2>Event-skabeloner</h2>
          <p>Opret genanvendelige skabeloner og opret hurtigt events på nye datoer og lokationer.</p>
        </header>

        <div class="panel-actions">
          <button mat-raised-button color="primary" (click)="startCreate()">
            <mat-icon>add</mat-icon>
            Opret ny skabelon
          </button>
        </div>

        <app-dynamic-table
          #templatesTable
          [dynamicInput]="templatesDynamicInput">
        </app-dynamic-table>
    </section>

    <!-- Trin 2: Definer skabelon -->
    @if (wizardStep === 2) {
      <section class="wizard-panel">
        <header class="panel-header">
          <h2>{{ mode === 'edit' ? 'Rediger skabelon' : 'Opret skabelon' }}</h2>
          <p>Definer event-indholdet. Datoer og lokationer angives ved oprettelse af events.</p>
        </header>

        <div class="form-section hide-survey-submit">
          <app-dynamic-form
            #templateForm
            [dynamicInput]="{
              'surveyJSON': { type: 'surveyJSON', value: getTemplateCreateFormConfig() },
              'formReceiveFunction': { type: 'text', value: '' },
              'settings': { type: 'json', value: templateFormSettings },
              'modalData': { type: 'json', value: templateFormData }
            }">
          </app-dynamic-form>
        </div>

        <footer class="panel-footer">
          <button mat-button type="button" (click)="backToList()" [disabled]="isProcessing">
            Annuller
          </button>
          <button mat-stroked-button color="primary" type="button"
                  (click)="saveTemplate(false)" [disabled]="isProcessing">
            Gem skabelon
          </button>
          <button mat-raised-button color="primary" type="button"
                  (click)="saveTemplate(true)" [disabled]="isProcessing">
            Gem og opret events
          </button>
        </footer>
      </section>
    }

    <!-- Trin 3: Opret events (holdes i DOM mellem trin 3 og 4 så værdier bevares) -->
    @if (wizardStep === 3 || wizardStep === 4) {
      <section class="wizard-panel" [hidden]="wizardStep !== 3">
        <header class="panel-header">
          <h2>Opret events: {{ selectedTemplate?.name }}</h2>
          @if (isEphemeralEventCopy) {
            <p class="ephemeral-hint">Engangskopi — indholdet gemmes ikke som skabelon.</p>
          }
          <p>Tilpas beskrivelse og tilføj datoer. Lokation kan overskrives per dato – ellers bruges skabelonens standard.</p>
        </header>

        <div class="form-section hide-survey-submit">
          <app-dynamic-form
            #copyForm
            [dynamicInput]="{
              'surveyJSON': { type: 'surveyJSON', value: getTemplateCopyFormConfig() },
              'formReceiveFunction': { type: 'text', value: '' },
              'settings': { type: 'json', value: copyFormSettings },
              'modalData': { type: 'json', value: copyFormData }
            }">
          </app-dynamic-form>
        </div>

        <footer class="panel-footer">
          <button mat-button type="button" (click)="backToList()" [disabled]="isProcessing">
            Annuller
          </button>
          <button mat-raised-button color="primary" type="button"
                  (click)="proceedToConfirm()" [disabled]="isProcessing">
            Næste: Bekræft
          </button>
        </footer>
      </section>
    }

    <!-- Trin 4: Bekræft -->
    @if (wizardStep === 4) {
      <section class="wizard-panel">
        <header class="panel-header">
          <h2>Bekræft oprettelse</h2>
          <p>
            @if (isEphemeralEventCopy) {
              Gennemgå engangskopi-events baseret på "{{ selectedTemplate?.name }}".
            } @else {
              Gennemgå events der oprettes fra skabelonen "{{ selectedTemplate?.name }}".
            }
          </p>
        </header>

        <div class="confirm-summary">
          <p class="summary-count">
            <strong>{{ getCopyInstances().length }}</strong> event(s) oprettes som uafhængige enkelt-dages events.
          </p>

          <ul class="instance-list">
            @for (instance of getCopyInstances(); track $index; let i = $index) {
              <li class="instance-item">
                <span class="instance-index">{{ i + 1 }}.</span>
                <span class="instance-date">{{ formatDateTime(instance.copy_start_date) }}</span>
                <span class="instance-time">– {{ instance.copy_end_time || selectedTemplate?.template_data?.['end_time'] }}</span>
                @if (instance.sub_event_location) {
                  <span class="instance-location">({{ instance.sub_event_location === 'manual' ? instance.sub_event_manual_location : 'Valgt lokation' }})</span>
                } @else {
                  <span class="instance-location">(skabelonens lokation)</span>
                }
              </li>
            }
          </ul>
        </div>

        <footer class="panel-footer">
          <button mat-button type="button" (click)="goToStep(3)" [disabled]="isProcessing">
            Tilbage
          </button>
          <button mat-raised-button color="primary" type="button"
                  (click)="createEvents()" [disabled]="isProcessing">
            @if (!isProcessing) {
              Opret {{ getCopyInstances().length }} event(s)
            } @else {
              Opretter...
            }
          </button>
        </footer>
      </section>
    }
  }
</div>
